/**
 * TourProvider - React Context for managing product tour state
 * Handles tour visibility, step tracking, and localStorage persistence
 * 
 * FIXED: 
 * - Skip tour now properly saves to localStorage and prevents restart
 * - Added authentication check to prevent tour from starting on login page
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { auth } from '../../auth';

const TOUR_STORAGE_KEY = 'heat_tour_completed_v1';

interface TourContextType {
  isRunning: boolean;
  currentStep: number;
  startTour: () => void;
  stopTour: () => void;
  skipTour: () => void;
  completeTour: () => void;
  setCurrentStep: (step: number) => void;
  hasCompletedTour: boolean;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

interface TourProviderProps {
  children: ReactNode;
}

export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasCompletedTour, setHasCompletedTour] = useState(true); // Default to true to prevent flash
  
  // Use ref to track if auto-start has been attempted (prevents re-triggering on navigation)
  const autoStartAttempted = useRef(false);

  // Check localStorage on mount - only auto-start once per session
  useEffect(() => {
    // CRITICAL: Only initialize tour if user is authenticated
    if (!auth.isAuthenticated()) {
      console.log('[Tour] User not authenticated, skipping tour initialization');
      return;
    }

    const completed = localStorage.getItem(TOUR_STORAGE_KEY);
    const isCompleted = completed === 'true';
    setHasCompletedTour(isCompleted);
    
    // Auto-start tour for first-time users ONLY if not already attempted
    if (!isCompleted && !autoStartAttempted.current) {
      autoStartAttempted.current = true;
      console.log('[Tour] Auto-starting tour for first-time user');
      const timer = setTimeout(() => {
        setIsRunning(true);
      }, 1500); // Give the dashboard time to load
      return () => clearTimeout(timer);
    }
  }, []);

  const startTour = useCallback(() => {
    // Double-check authentication before starting tour
    if (!auth.isAuthenticated()) {
      console.warn('[Tour] Cannot start tour - user not authenticated');
      return;
    }
    
    setCurrentStep(0);
    setIsRunning(true);
    // Mark that we're intentionally starting the tour
    autoStartAttempted.current = true;
    console.log('[Tour] Tour started manually');
  }, []);

  const stopTour = useCallback(() => {
    setIsRunning(false);
    console.log('[Tour] Tour stopped');
  }, []);

  // Skip tour - saves to localStorage so it won't restart
  const skipTour = useCallback(() => {
    setIsRunning(false);
    setCurrentStep(0);
    setHasCompletedTour(true);
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    console.log('[Tour] Skipped - saved to localStorage');
  }, []);

  const completeTour = useCallback(() => {
    setIsRunning(false);
    setCurrentStep(0);
    setHasCompletedTour(true);
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    console.log('[Tour] Completed - saved to localStorage');
  }, []);

  const value: TourContextType = {
    isRunning,
    currentStep,
    startTour,
    stopTour,
    skipTour,
    completeTour,
    setCurrentStep,
    hasCompletedTour,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};

export const useTour = (): TourContextType => {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

export default TourProvider;
